
export const cityToEmoji: Record<string, string> = {
  "Paris": "🗼",
  "London": "💂",
  "Rome": "🏛️",
  "Venice": "🛶",
  "Florence": "🎨",
  "Milan": "👠",
  "Berlin": "🍺",
  "Munich": "🥨",
  "Amsterdam": "🚲",
  "Brussels": "🍫",
  "Madrid": "💃",
  "Barcelona": "🥘",
  "Lisbon": "🚋",
  "Athens": "🏛️",
  "Istanbul": "🕌",
  "Cappadocia": "🎈",
  "Antalya": "🏖️",
  "Bodrum": "⛵",
  "Split": "🏛️",
  "Dubrovnik": "🏰",
  "Hvar": "🛥️",
  "Copenhagen": "🧜‍♀️",
  "Stockholm": "🇸🇪",
  "Oslo": "🏔️",
  "Reykjavik": "🌋",
  "New York": "🗽",
  "Tokyo": "🍣",
  "Kyoto": "⛩️",
  "Seoul": "🥢",
  "Singapore": "🦁",
  "Sydney": "🦘",
  "Melbourne": "☕",
  "Cairo": "☥",
  "Giza": "👤",
  "Marrakech": "🕌",
  "Cape Town": "🐧",
  "Rio de Janeiro": "💃",
  "Buenos Aires": "🥩",
  "Machu Picchu": "🦙",
  "Bali": "🏖️",
  "Phuket": "🏝️",
  "Bangkok": "🍜",
};

// ─── Country → Region mapping ─────────────────────────────────────────────────

/** Normalise a country name for lookup (same rules as countryFlag.ts). */
function normalizeCountry(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')        // strip diacritical marks
    .replace(/^the\s+/, '')        // strip leading "the "
    .replace(/\s*&\s*/g, ' and '); // & → and
}

/**
 * Culturally-aware country → travel region mapping.
 * Key opinionated overrides vs. strict geography:
 *   Turkey, Cyprus, Russia, Georgia, Armenia → Europe
 *   Israel, UAE, Gulf states, Azerbaijan      → Middle East
 *   Egypt, Morocco, Tunisia                   → Africa
 *   Mexico + Caribbean islands                → Central America
 *   All Asian sub-regions                     → Asia
 */
const COUNTRY_REGION: Record<string, string> = {
  // ── Europe ──────────────────────────────────────────────────────────────────
  france: 'Europe', germany: 'Europe', italy: 'Europe', spain: 'Europe',
  portugal: 'Europe', netherlands: 'Europe', belgium: 'Europe', switzerland: 'Europe',
  austria: 'Europe', greece: 'Europe',
  turkey: 'Europe', turkiye: 'Europe',          // cultural override
  croatia: 'Europe', slovenia: 'Europe', 'czech republic': 'Europe', czechia: 'Europe',
  poland: 'Europe', hungary: 'Europe', romania: 'Europe', bulgaria: 'Europe',
  serbia: 'Europe', montenegro: 'Europe', 'north macedonia': 'Europe', albania: 'Europe',
  'bosnia and herzegovina': 'Europe', kosovo: 'Europe',
  denmark: 'Europe', norway: 'Europe', sweden: 'Europe', finland: 'Europe', iceland: 'Europe',
  ireland: 'Europe', 'republic of ireland': 'Europe',
  'united kingdom': 'Europe', uk: 'Europe', england: 'Europe', scotland: 'Europe', wales: 'Europe',
  russia: 'Europe', ukraine: 'Europe', belarus: 'Europe', moldova: 'Europe',
  georgia: 'Europe', armenia: 'Europe',         // cultural override
  cyprus: 'Europe',                              // EU member
  malta: 'Europe', luxembourg: 'Europe', andorra: 'Europe', monaco: 'Europe',
  liechtenstein: 'Europe', 'san marino': 'Europe', 'vatican city': 'Europe', 'holy see': 'Europe',
  latvia: 'Europe', lithuania: 'Europe', estonia: 'Europe',
  slovakia: 'Europe', 'slovak republic': 'Europe',
  // ── Middle East ─────────────────────────────────────────────────────────────
  'united arab emirates': 'Middle East', uae: 'Middle East',
  'saudi arabia': 'Middle East', qatar: 'Middle East', kuwait: 'Middle East',
  bahrain: 'Middle East', oman: 'Middle East',
  jordan: 'Middle East', lebanon: 'Middle East', israel: 'Middle East',
  iraq: 'Middle East', iran: 'Middle East', syria: 'Middle East', yemen: 'Middle East',
  azerbaijan: 'Middle East', palestine: 'Middle East', 'palestinian territories': 'Middle East',
  // ── Africa ──────────────────────────────────────────────────────────────────
  egypt: 'Africa', morocco: 'Africa', tunisia: 'Africa', algeria: 'Africa', libya: 'Africa',
  'south africa': 'Africa', kenya: 'Africa', tanzania: 'Africa', ethiopia: 'Africa',
  nigeria: 'Africa', ghana: 'Africa', senegal: 'Africa', rwanda: 'Africa', uganda: 'Africa',
  zimbabwe: 'Africa', zambia: 'Africa', mozambique: 'Africa', angola: 'Africa',
  cameroon: 'Africa', 'ivory coast': 'Africa', "cote d'ivoire": 'Africa',
  madagascar: 'Africa', mauritius: 'Africa', seychelles: 'Africa',
  'cape verde': 'Africa', 'cabo verde': 'Africa',
  namibia: 'Africa', botswana: 'Africa', 'sierra leone': 'Africa', liberia: 'Africa',
  guinea: 'Africa', mali: 'Africa', niger: 'Africa', chad: 'Africa', sudan: 'Africa',
  'south sudan': 'Africa', somalia: 'Africa', eritrea: 'Africa', djibouti: 'Africa',
  malawi: 'Africa', 'burkina faso': 'Africa', benin: 'Africa', togo: 'Africa',
  gabon: 'Africa', congo: 'Africa', 'democratic republic of the congo': 'Africa',
  burundi: 'Africa', 'central african republic': 'Africa', comoros: 'Africa',
  'equatorial guinea': 'Africa', 'guinea-bissau': 'Africa', 'sao tome and principe': 'Africa',
  lesotho: 'Africa', eswatini: 'Africa', swaziland: 'Africa',
  // ── Asia (all sub-regions collapse to "Asia") ────────────────────────────────
  // East Asia
  japan: 'Asia', 'south korea': 'Asia', china: 'Asia', taiwan: 'Asia',
  'hong kong': 'Asia', macao: 'Asia', macau: 'Asia', mongolia: 'Asia',
  // Southeast Asia
  thailand: 'Asia', vietnam: 'Asia', cambodia: 'Asia', laos: 'Asia', 'lao pdr': 'Asia',
  myanmar: 'Asia', burma: 'Asia', malaysia: 'Asia', singapore: 'Asia',
  indonesia: 'Asia', philippines: 'Asia', brunei: 'Asia', 'timor-leste': 'Asia', 'east timor': 'Asia',
  // South Asia
  india: 'Asia', pakistan: 'Asia', bangladesh: 'Asia', 'sri lanka': 'Asia',
  nepal: 'Asia', bhutan: 'Asia', maldives: 'Asia',
  // Central Asia
  afghanistan: 'Asia', uzbekistan: 'Asia', kazakhstan: 'Asia',
  kyrgyzstan: 'Asia', tajikistan: 'Asia', turkmenistan: 'Asia',
  // ── Oceania ──────────────────────────────────────────────────────────────────
  australia: 'Oceania', 'new zealand': 'Oceania', fiji: 'Oceania',
  'papua new guinea': 'Oceania', samoa: 'Oceania', tonga: 'Oceania',
  vanuatu: 'Oceania', 'solomon islands': 'Oceania', kiribati: 'Oceania',
  nauru: 'Oceania', tuvalu: 'Oceania', palau: 'Oceania', 'marshall islands': 'Oceania',
  micronesia: 'Oceania', 'new caledonia': 'Oceania', 'french polynesia': 'Oceania',
  // ── North America ────────────────────────────────────────────────────────────
  'united states': 'North America', usa: 'North America', america: 'North America',
  canada: 'North America',
  // ── Central America (incl. Mexico + Caribbean) ───────────────────────────────
  mexico: 'Central America',
  guatemala: 'Central America', belize: 'Central America', honduras: 'Central America',
  'el salvador': 'Central America', nicaragua: 'Central America',
  'costa rica': 'Central America', panama: 'Central America',
  cuba: 'Central America', jamaica: 'Central America', haiti: 'Central America',
  'dominican republic': 'Central America', 'trinidad and tobago': 'Central America',
  barbados: 'Central America', bahamas: 'Central America', grenada: 'Central America',
  'saint lucia': 'Central America', 'saint kitts and nevis': 'Central America',
  'saint vincent and the grenadines': 'Central America',
  dominica: 'Central America', antigua: 'Central America', 'antigua and barbuda': 'Central America',
  'puerto rico': 'Central America',
  // ── Latin America (South America) ────────────────────────────────────────────
  brazil: 'Latin America', argentina: 'Latin America', chile: 'Latin America',
  peru: 'Latin America', colombia: 'Latin America', venezuela: 'Latin America',
  ecuador: 'Latin America', bolivia: 'Latin America', uruguay: 'Latin America',
  paraguay: 'Latin America', guyana: 'Latin America', suriname: 'Latin America',
};

/**
 * Return the travel region for a given country name.
 * Handles any capitalisation, diacritics, and common aliases.
 * Returns undefined for unrecognised countries (caller decides how to handle).
 */
/**
 * Names this map is missing that getCountryFlag's own alias table already
 * knows. Kept as a small patch rather than a second full list: the two tables
 * are keyed differently (region by name, flags by ISO code), so they will
 * always need reconciling somewhere, and somewhere small is better than a
 * duplicate of either.
 */
const REGION_ALIAS: Record<string, string> = {
  holland: 'netherlands',
  espana: 'spain',
  'viet nam': 'vietnam',
  korea: 'south korea',
  'republic of korea': 'south korea',
  'great britain': 'united kingdom',
  england: 'united kingdom',
  scotland: 'united kingdom',
  wales: 'united kingdom',
};

export function getCountryRegion(countryName: string): string | undefined {
  if (!countryName?.trim()) return undefined;
  const key = normalizeCountry(countryName);
  return COUNTRY_REGION[key] ?? COUNTRY_REGION[REGION_ALIAS[key] ?? ''];
}

/**
 * Keyword icons for a destination with no emoji of its own, in priority order.
 *
 * Matched as WHOLE WORDS. These used to be plain substring `includes()` checks,
 * which fired inside longer words and produced confidently wrong icons for
 * ordinary places: Porto, Portugal, Portland and Newport all got an anchor off
 * "port", Carlisle got an island off "isle", Sheffield and Springfield got a
 * tractor off "field", Bayswater a canoe, Alpharetta a mountain. The keywords
 * were right; matching them mid-word was not.
 */
const LOCATION_KEYWORDS: [string, string[]][] = [
  ['🏖️', ['beach', 'coast', 'shore', 'riviera']],
  ['🏔️', ['mountain', 'alp', 'peak', 'summit']],
  ['🏝️', ['island', 'isle']],
  ['🛶', ['lake', 'loch', 'bay']],
  ['🌳', ['park', 'garden', 'forest', 'wood']],
  ['🎨', ['museum', 'gallery', 'exhibition']],
  ['🏰', ['castle', 'palace', 'fort', 'citadel']],
  ['⛪', ['church', 'cathedral', 'basilica', 'chapel']],
  ['🕌', ['mosque']],
  ['⛩️', ['temple', 'shrine', 'pagoda']],
  ['🎓', ['university', 'college', 'school', 'campus']],
  // "terminal" belongs to the airport: it was listed under both, and the
  // station rule could never reach it.
  ['✈️', ['airport', 'terminal']],
  ['🚉', ['station', 'stop']],
  ['⚓', ['port', 'harbor', 'harbour', 'marina', 'dock']],
  ['🏨', ['villa', 'resort', 'hotel', 'stay']],
  ['🛍️', ['market', 'bazaar', 'souk']],
  ['🍷', ['winery', 'vineyard']],
  ['🚜', ['farm', 'ranch', 'field']],
];

/** Whole word, with an optional plural — "Islands" and "Gardens" are still
 *  islands and gardens, and "Portugal" is still not a port. */
const hasWord = (haystack: string, word: string) => new RegExp(`\\b${word}s?\\b`).test(haystack);

export function getLocationEmoji(location: string): string {
  if (!location) return "📍";

  // Exact city matches first: the city table is the specific answer, the
  // keywords below are only the fallback.
  const parts = location.split(',').map(p => p.trim());
  for (const part of parts) {
    if (cityToEmoji[part]) return cityToEmoji[part];
  }

  const lower = location.toLowerCase();
  for (const [emoji, words] of LOCATION_KEYWORDS) {
    if (words.some(word => hasWord(lower, word))) return emoji;
  }
  return "📍";
}
